import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import Layout from "../../components/Layout";
import { useAuth } from "../../context/AuthContext";
import { notificationsService } from "../../services/notifications.service";
import { Notification } from "../../types/notification";
import { dashboardService } from "../../services/dashboard.service";
import { DashboardStats } from "../../types/dashboard";
import Button from "../../components/ui/Button";
import { Permission } from "../../permissions/permission.enum";
import Card from "../../components/ui/Card";

const Dashboard = () => {
  const { t } = useTranslation(["common", "home"]);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [latest, setLatest] = useState<Notification[]>([]);
  const [loadingNotifs, setLoadingNotifs] = useState(true);
  const [errorNotifs, setErrorNotifs] = useState("");
  const [stats, setStats] = useState<DashboardStats[]>([]);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await dashboardService.getStats();
        setStats(data);
      } catch (error) {
        console.error("Failed to fetch dashboard stats", error);
      } finally {
        setLoadingStats(false);
      }
    };
    if (hasPermission(Permission.DASHBOARD_READ)) {
      fetchStats();
    } else {
      setLoadingStats(false);
    }
  }, [hasPermission]);

  const notificationsPath = useMemo(() => {
    if (hasPermission(Permission.ADMIN_NOTIFICATIONS_READ))
      return "/notifications/admin";
    if (hasPermission(Permission.USER_NOTIFICATIONS_READ))
      return "/notifications/user";
    return null;
  }, [hasPermission]);

  useEffect(() => {
    const load = async () => {
      setLoadingNotifs(true);
      setErrorNotifs("");
      try {
        if (notificationsPath === "/notifications/admin") {
          const res = await notificationsService.listAdmin({
            page: 1,
            take: 5,
          });
          setLatest(res.data);
        } else if (notificationsPath === "/notifications/user") {
          const res = await notificationsService.listUser({ page: 1, take: 5 });
          setLatest(res.data);
        } else {
          setLatest([]);
        }
      } catch {
        setErrorNotifs(t("failed_to_load_notifications"));
      } finally {
        setLoadingNotifs(false);
      }
    };
    load();
  }, [notificationsPath, t]);

  return (
    <Layout title={t("dashboard")}>
      <div className="space-y-6">
        {/* Notifications Section */}
        <div className="grid grid-cols-1">
          <Card className="p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t("latest_notifications")}
              </h2>
              {notificationsPath && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate(notificationsPath)}
                >
                  {t("view_all")}
                </Button>
              )}
            </div>

            {loadingNotifs ? (
              <div className="text-sm text-gray-600 dark:text-gray-300">
                {t("loading")}
              </div>
            ) : errorNotifs ? (
              <div className="text-sm text-red-600">{errorNotifs}</div>
            ) : latest.length === 0 ? (
              <div className="text-sm text-gray-600 dark:text-gray-300">
                {t("noData")}
              </div>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {latest.map((n) => (
                  <li key={n.id} className="py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">
                            {n.title}
                          </span>
                          {!n.isRead && (
                            <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                              {t("unread")}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                          {n.message}
                        </p>
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {new Date(n.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* User Statistics Chart */}
          <Card className="p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              {t("user_statistics")}
            </h2>
            {loadingStats ? (
              <div className="flex justify-center items-center h-[300px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={stats}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#374151"
                      opacity={0.1}
                    />
                    <XAxis dataKey="name" stroke="#6B7280" />
                    <YAxis stroke="#6B7280" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#1F2937",
                        border: "none",
                        borderRadius: "8px",
                        color: "#F3F4F6",
                      }}
                      itemStyle={{ color: "#F3F4F6" }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="activeUsers"
                      stroke="#8884d8"
                      activeDot={{ r: 8 }}
                      name={t("active_users")}
                    />
                    <Line
                      type="monotone"
                      dataKey="newUsers"
                      stroke="#82ca9d"
                      name={t("new_users")}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          {/* System Activity Chart */}
          <Card className="p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              {t("system_activity")}
            </h2>
            {loadingStats ? (
              <div className="flex justify-center items-center h-[300px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stats}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#374151"
                      opacity={0.1}
                    />
                    <XAxis dataKey="name" stroke="#6B7280" />
                    <YAxis stroke="#6B7280" />
                    <Tooltip
                      cursor={{ fill: "transparent" }}
                      contentStyle={{
                        backgroundColor: "#1F2937",
                        border: "none",
                        borderRadius: "8px",
                        color: "#F3F4F6",
                      }}
                      itemStyle={{ color: "#F3F4F6" }}
                    />
                    <Legend />
                    <Bar
                      dataKey="auditLogs"
                      fill="#8884d8"
                      name={t("audit_logs")}
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="notifications"
                      fill="#82ca9d"
                      name={t("notifications_sent")}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
